import { ChangeDetectionStrategy, Component, Injector, Input, OnInit } from '@angular/core';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { IFormQuestion } from '@fi-shared/fi-form-shared/fi-form-shared';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { FormQuestionType } from '@fi-shared/fi-form-shared/fi-form-enums';
import { FiFormHelperService } from '@fi-shared/fi-form-shared/fi-form-helper.service';

@Component({
    selector: 'fi-choice-question-validate',
    template: ``,
    styleUrls: [],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiChoiceQuestionValidateComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiChoiceQuestionValidateComponent,
        },
    ],
    standalone: false,
})
export class FiChoiceQuestionValidateComponent extends ControlValueAccessorValidatorBase<IFormQuestion> {
    @Input() isFormTranslated: boolean;
    @Input() defaultLanguageId: number;
    @Input() selectedLanguageId: number;

    get question() {
        return this.ngModel;
    }

    constructor(
        private injector: Injector,
        private formHelperService: FiFormHelperService,
    ) {
        super(injector);
    }

    isSingleChoiceQuestion(question: IFormQuestion): boolean {
        return question.Type === FormQuestionType.SingleChoice;
    }

    isMultipleChoiceQuestion(question: IFormQuestion): boolean {
        return question.Type === FormQuestionType.MultipleChoice;
    }

    private getPreferredContentLabelIndex(): number {
        return this.question.Names.findIndex((name) => name.ContentLanguageId === this.selectedLanguageId);
    }

    doValidate(c: AbstractControl): ValidationErrors {
        const validationResult: ValidationErrors = {
            questionNoOptions:
                (!this.question.PresetOptions || !this.question.PresetOptions.length) &&
                this.isMultipleChoiceQuestion(this.question),
            singleChoicePresetOptionAmount:
                (!this.question.PresetOptions ||
                    !this.question.PresetOptions.length ||
                    this.question.PresetOptions.length <= 1) &&
                this.isSingleChoiceQuestion(this.question),
            questionOptionsNotTranslated: this.isSomeOptionsNotTranslated(),
        };
        if (this.question.PresetOptions.length >= 2 && this.isSingleChoiceQuestion(this.question)) {
            if (!this.isFormTranslated) {
                const i = this.getPreferredContentLabelIndex();
                if (i !== -1) {
                    // selectedLanguageId is undefined on 1st validate
                    const options = this.question.PresetOptions.map((option) => option.Names[i].Value);
                    const uniqueOptions = options.filter((option, index, self) => {
                        return self.indexOf(option) === index;
                    });
                    if (uniqueOptions.length <= 1) validationResult['singleChoicePresetOptionAmount'] = true;
                }
            }
        }
        return validationResult;
    }

    private isSomeOptionsNotTranslated(): boolean {
        return (
            this.question.PresetOptions.length &&
            this.question.PresetOptions.some((option) => {
                return option.Names.some((name) => {
                    return this.formHelperService.isQuestionNameRequired(name, this.isFormTranslated) && !name.Value;
                });
            })
        );
    }
}
